
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Card from '../../components/base/Card';
import Tag from '../../components/base/Tag';
import SearchBar from '../../components/base/SearchBar';
import Button from '../../components/base/Button';
import { supabase } from '../../lib/supabase';

export default function Blog() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [blogs, setBlogs] = useState([]);
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [allTags, setAllTags] = useState<Array<{tag: string, count: number}>>([]);

  // SEO設定
  useEffect(() => {
    document.title = "記事まとめ - 兵庫県政検証サイト";
    
    // メタタグの設定
    let descriptionMeta = document.querySelector('meta[name="description"]');
    if (!descriptionMeta) {
      descriptionMeta = document.createElement('meta');
      descriptionMeta.setAttribute('name', 'description');
      document.head.appendChild(descriptionMeta);
    }
    descriptionMeta.setAttribute('content', '兵庫県政に関する注目記事をピックアップしてコメント付きでまとめています。会見、ニュース、ファクトチェック記事を分類して提供。');
    
    // Schema.org JSON-LD for Blog Page
    const blogPageSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "記事まとめ",
      "description": "兵庫県政に関する注目記事をピックアップしてコメント付きでまとめています。",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/blog`,
      "inLanguage": "ja",
      "isPartOf": {
        "@type": "WebSite",
        "name": "兵庫県政検証サイト",
        "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/`
      },
      "mainEntity": {
        "@type": "Blog",
        "name": "兵庫県政記事まとめ",
        "description": "県政に関する記事をカテゴリ別に整理したブログ"
      }
    };
    
    let existingScript = document.querySelector('script[data-schema="blog"]');
    if (existingScript) {
      existingScript.remove();
    }
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-schema', 'blog');
    script.textContent = JSON.stringify(blogPageSchema);
    document.head.appendChild(script);
    
    return () => {
      const scriptToRemove = document.querySelector('script[data-schema="blog"]');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, []);

  // 新しいカテゴリーリスト
  const categories = ['所感', '動画', '報道', 'note記事', 'post', 'その他'];

  // URLパラメータからタグを取得
  useEffect(() => {
    const tagParam = searchParams.get('tag');
    if (tagParam) {
      setSelectedTag(decodeURIComponent(tagParam));
    }
  }, [searchParams]);

  // Supabaseからデータを取得
  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const { data, error } = await supabase
          .from('blogs')
          .select('*')
          .order('published_at', { ascending: false });
        
        if (error) {
          console.error('Blogs fetch error:', error);
        } else {
          setBlogs(data || []);
          setFilteredBlogs(data || []);
          
          // タグ情報を抽出
          extractTags(data || []);
        }
      } catch (error) {
        console.error('Data fetch error:', error);
      }
    };

    fetchBlogs();

    // リアルタイム更新の設定
    const subscription = supabase
      .channel('blogs_changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'blogs' },
        () => fetchBlogs()
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // タグ情報を抽出
  const extractTags = (blogList: any[]) => {
    const tagCounts: Record<string, number> = {};
    
    blogList.forEach(blog => {
      if (blog.tags && Array.isArray(blog.tags)) {
        blog.tags.forEach(tag => {
          if (tag && tag.trim()) {
            const cleanTag = tag.trim();
            tagCounts[cleanTag] = (tagCounts[cleanTag] || 0) + 1;
          }
        });
      }
    });

    const sortedTags = Object.entries(tagCounts)
      .map(([tag, count]) => ({ tag, count }))
      .sort((a, b) => b.count - a.count);

    setAllTags(sortedTags);
  };

  // フィルター条件が変更された時にフィルタリングを再実行
  useEffect(() => {
    filterBlogs(selectedCategory, selectedTag, searchQuery, blogs);
  }, [selectedCategory, selectedTag, searchQuery, blogs]);

  const handleCategoryFilter = (category: string) => {
    setSelectedCategory(category);
    // カテゴリーを選択したらタグ選択をクリア
    if (category !== selectedCategory) {
      setSelectedTag('');
      searchParams.delete('tag');
      setSearchParams(searchParams);
    }
  };

  const handleTagFilter = (tag: string) => {
    setSelectedTag(tag);
    // タグを選択したらカテゴリー選択をクリア
    if (tag !== selectedTag) {
      setSelectedCategory('');
    }
    // URLパラメータを更新
    if (tag) {
      searchParams.set('tag', encodeURIComponent(tag));
    } else {
      searchParams.delete('tag');
    }
    setSearchParams(searchParams);
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const filterBlogs = (category: string, tag: string, query: string, blogList = blogs) => {
    let filtered = [...blogList];

    if (category) {
      filtered = filtered.filter(blog => blog.category === category);
    }

    if (tag) {
      filtered = filtered.filter(blog => 
        blog.tags && Array.isArray(blog.tags) && blog.tags.includes(tag)
      );
    }

    if (query) {
      filtered = filtered.filter(blog =>
        blog.title.toLowerCase().includes(query.toLowerCase()) ||
        blog.excerpt.toLowerCase().includes(query.toLowerCase()) ||
        blog.external_links?.some(link => 
          link.title.toLowerCase().includes(query.toLowerCase())
        )
      );
    }

    setFilteredBlogs(filtered);
  };

  // フィルターをクリア
  const clearFilters = () => {
    setSelectedCategory('');
    setSelectedTag('');
    setSearchQuery('');
    searchParams.delete('tag');
    setSearchParams(searchParams);
  };

  // 外部リンク用のURL生成
  const getShareableUrl = (blog: any) => {
    return `${window.location.origin}/blog#${blog.id}`;
  };

  // クリップボードにコピー
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      // 簡単な成功フィードバック
      const button = document.activeElement as HTMLElement;
      if (button) {
        button.textContent = 'コピー完了!';
        setTimeout(() => {
          button.innerHTML = '<i class="ri-link mr-1"></i>リンクをコピー';
        }, 2000);
      }
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  // SNSシェア用URL生成
  const getTwitterShareUrl = (blog: any) => {
    const url = getShareableUrl(blog);
    const text = `${blog.title} - 兵庫県政検証サイト`;
    return `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
  };

  const getFacebookShareUrl = (blog: any) => {
    const url = getShareableUrl(blog);
    return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
  };

  const getLineShareUrl = (blog: any) => {
    const url = getShareableUrl(blog);
    const text = `${blog.title} - 兵庫県政検証サイト`;
    return `https://social-plugins.line.me/lineit/share?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">記事まとめ</h1>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            注目の記事をピックアップして、コメント付きでまとめています
          </p>
        </div>

        {/* Search and Filters */}
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-6">
            <div className="flex-1">
              <SearchBar
                placeholder="記事を検索..."
                value={searchQuery}
                onChange={setSearchQuery}
                className="w-full"
              />
            </div>
          </div>

          {/* Category Filters */}
          <div className="mt-6">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">カテゴリで絞り込み:</h3>
            <div className="flex flex-wrap gap-2">
              <Button
                variant={selectedCategory === '' ? 'primary' : 'outline'}
                size="sm"
                onClick={() => handleCategoryFilter('')}
              >
                すべて
              </Button>
              {categories.map((category) => (
                <Button
                  key={category}
                  variant={selectedCategory === category ? 'primary' : 'outline'}
                  size="sm"
                  onClick={() => handleCategoryFilter(category)}
                >
                  {category}
                </Button>
              ))}
            </div>
          </div>

          {/* Tag Filters */}
          {allTags.length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">タグで絞り込み:</h3>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant={selectedTag === '' ? 'primary' : 'outline'}
                  size="sm"
                  onClick={() => handleTagFilter('')}
                >
                  すべて
                </Button>
                {allTags.slice(0, 10).map(({ tag, count }) => (
                  <Button
                    key={tag}
                    variant={selectedTag === tag ? 'primary' : 'outline'}
                    size="sm"
                    onClick={() => handleTagFilter(tag)}
                    title={`${count}件の記事で使用`}
                  >
                    {tag} ({count})
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Active Filters */}
          {(selectedCategory || selectedTag || searchQuery) && (
            <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2 text-sm">
                  <span className="font-medium text-blue-800 dark:text-blue-200">適用中のフィルター:</span>
                  {selectedCategory && (
                    <span className="px-2 py-1 bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 rounded">
                      カテゴリ: {selectedCategory}
                    </span>
                  )}
                  {selectedTag && (
                    <span className="px-2 py-1 bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200 rounded">
                      タグ: {selectedTag}
                    </span>
                  )}
                  {searchQuery && (
                    <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded">
                      検索: {searchQuery}
                    </span>
                  )}
                </div>
                <Button size="sm" variant="outline" onClick={clearFilters}>
                  すべてクリア
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Results */}
        <div className="mb-6">
          <p className="text-gray-600 dark:text-gray-400">
            {filteredBlogs.length}件の記事が見つかりました
            {(selectedCategory || selectedTag) && (
              <span className="ml-2 text-sm">
                ({selectedCategory && `カテゴリ: ${selectedCategory}`}
                {selectedCategory && selectedTag && ', '}
                {selectedTag && `タグ: ${selectedTag}`})
              </span>
            )}
          </p>
        </div>

        {/* Blog Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {filteredBlogs.map((blog) => (
            <Card key={blog.id} className="overflow-hidden hover:shadow-lg transition-all duration-200" id={`blog-${blog.id}`}>
              <div className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <Tag variant="category">{blog.category}</Tag>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {new Date(blog.published_at).toLocaleDateString('ja-JP')}
                  </span>
                </div>
                
                {/* 記事タイトル（大きめの文字でURLリンク付き） */}
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 line-clamp-2">
                  {blog.url ? (
                    <a
                      href={blog.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 dark:text-blue-400 hover:underline cursor-pointer"
                    >
                      {blog.title}
                    </a>
                  ) : (
                    blog.title
                  )}
                </h3>
                
                {/* 概要（改行を反映） */}
                {blog.excerpt && (
                  <div className="mb-4">
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">概要:</div>
                    <p className="text-gray-600 dark:text-gray-400 text-sm line-clamp-7 whitespace-pre-wrap">
                      {blog.excerpt}
                    </p>
                  </div>
                )}
                
                {/* 筆者 */}
                <div className="mb-4">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">筆者: </span>
                  <span className="text-sm text-gray-600 dark:text-gray-400">{blog.author}</span>
                </div>
                
                {/* カテゴリ */}
                <div className="mb-4">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">カテゴリ: </span>
                  <Tag variant="category">{blog.category}</Tag>
                </div>

                {/* タグ */}
                {blog.tags && blog.tags.length > 0 && (
                  <div className="mb-4">
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">タグ:</div>
                    <div className="flex flex-wrap gap-1">
                      {blog.tags.map((tag: string) => (
                        <button
                          key={tag}
                          onClick={() => handleTagFilter(tag)}
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium cursor-pointer transition-colors whitespace-nowrap ${
                            selectedTag === tag
                              ? 'bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200'
                              : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                          }`}
                        >
                          <i className="ri-price-tag-3-line mr-1"></i>
                          {tag}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* 関連記事 */}
                {blog.external_links && blog.external_links.length > 0 && (
                  <div className="mb-4">
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      関連記事 ({blog.external_links.length}件):
                    </div>
                    <div className="space-y-2">
                      {blog.external_links.map((link: any, index: number) => (
                        <div key={index}>
                          <a
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-blue-600 dark:text-blue-400 hover:underline cursor-pointer block"
                          >
                            {link.title}
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 内容紹介（旧まとめコメント） */}
                {blog.body && (
                  <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      内容紹介:
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 text-sm whitespace-pre-wrap">
                      {blog.body}
                    </p>
                  </div>
                )}

                {/* Share Section */}
                <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                  <div className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-2 flex items-center">
                    <i className="ri-share-line mr-1"></i>
                    拡散にご協力ください
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => copyToClipboard(getShareableUrl(blog))}
                      className="flex items-center px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md text-xs hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-link mr-1"></i>リンクをコピー
                    </button>
                    <a
                      href={getTwitterShareUrl(blog)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center px-3 py-1.5 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-md text-xs hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-twitter-x-line mr-1"></i>X (Twitter)
                    </a>
                    <a
                      href={getFacebookShareUrl(blog)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center px-3 py-1.5 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-md text-xs hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-facebook-line mr-1"></i>Facebook
                    </a>
                    <a
                      href={getLineShareUrl(blog)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center px-3 py-1.5 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded-md text-xs hover:bg-green-200 dark:hover:bg-green-800 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-line-line mr-1"></i>LINE
                    </a>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {filteredBlogs.length === 0 && (
          <div className="text-center py-12">
            <i className="ri-search-line text-6xl text-gray-300 dark:text-gray-600 mb-4"></i>
            <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">
              該当する記事が見つかりませんでした
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              検索条件を変更してもう一度お試しください
            </p>
            {(selectedCategory || selectedTag || searchQuery) && (
              <Button onClick={clearFilters}>
                フィルターをクリアして全ての記事を表示
              </Button>
            )}
          </div>
        )}

        {/* Featured Categories */}
        <div className="mt-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">カテゴリ別記事数</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {categories.map((category) => {
              const categoryCount = blogs.filter(blog => blog.category === category).length;
              return (
                <button
                  key={category}
                  onClick={() => handleCategoryFilter(category)}
                  className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow cursor-pointer text-left"
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{category}</h3>
                    <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">{categoryCount}</span>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">記事</p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Share Instructions */}
        <div className="mt-16 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg p-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              情報拡散にご協力ください
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-2xl mx-auto">
              兵庫県政の問題を多くの方に知っていただくため、記事の共有にご協力ください。
              各記事の共有ボタンから簡単にSNSでシェアできます。
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-600 dark:text-gray-400">
              <div className="flex items-center">
                <i className="ri-link mr-2 text-gray-500"></i>
                直接リンクコピー
              </div>
              <div className="flex items-center">
                <i className="ri-twitter-x-line mr-2 text-blue-500"></i>
                X (Twitter) 投稿
              </div>
              <div className="flex items-center">
                <i className="ri-facebook-line mr-2 text-blue-600"></i>
                Facebook 投稿
              </div>
              <div className="flex items-center">
                <i className="ri-line-line mr-2 text-green-500"></i>
                LINE で送信
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
